// components/scans/ScanCategories.jsx
import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Stack,
  Autocomplete,
  TextField,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Category as CategoryIcon,
  FilterList,
  Clear as ClearIcon,
  FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { scanCategoryAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import NoContent from '../../components/common/NoContent';
import NoPrivilege from '../../components/common/NoPrivilege';
import SearchBar from '../../components/common/SearchBar';
import ScanCategoriesTable from './ScanCategoriesTable';
import AddScanCategoryDialog from './AddScanCategoryDialog';

const ScanCategories = () => {
  const { t } = useTranslation();
  const { user, isSuperAdmin } = useAuth();

  // Filter options
  const SORT_BY_OPTIONS = [
    { value: 'name', label: t('common.name', 'Name') },
    { value: 'createdAt', label: t('common.createdAt', 'Created At') },
  ];

  const SORT_ORDER_OPTIONS = [
    { value: 'asc', label: t('common.ascending', 'Ascending') },
    { value: 'desc', label: t('common.descending', 'Descending') },
  ];

  const STATUS_OPTIONS = [
    { value: 'all', label: t('common.all', 'All') },
    { value: 'active', label: t('common.active', 'Active') },
    { value: 'inactive', label: t('common.inactive', 'Inactive') },
  ];

  // States
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [privilegeError, setPrivilegeError] = useState(false);

  // Export State
  const [exporting, setExporting] = useState(false);

  // Consolidated filter state
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    sortBy: 'name',
    sortOrder: 'asc',
  });

  // Permissions
  const canView = isSuperAdmin || user?.privileges?.some(p => p.module === 'scanCategories' && p.operations.includes('view'));
  const canAdd = isSuperAdmin || user?.privileges?.some(p => p.module === 'scanCategories' && p.operations.includes('create'));
  const canEdit = isSuperAdmin || user?.privileges?.some(p => p.module === 'scanCategories' && p.operations.includes('update'));
  const canDelete = isSuperAdmin || user?.privileges?.some(p => p.module === 'scanCategories' && p.operations.includes('delete'));
  const canExport = canView;

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    setPrivilegeError(false);

    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
      };

      if (filters.search) {
        params.search = filters.search;
      }

      if (filters.status !== 'all') {
        params.status = filters.status;
      }

      const response = await scanCategoryAPI.getAll(params);
      setCategories(response.data.data || []);
      setTotal(response.data.pagination?.total || 0);
    } catch (err) {
      const backendMessage = err.response?.data?.message;
      if (backendMessage && backendMessage.toLowerCase().includes('insufficient privileges')) {
        setPrivilegeError(true);
      } else {
        toast.error(backendMessage || t('scanCategories.fetchError', 'Failed to fetch categories'));
      }
      setCategories([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters, canView, t]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Filter handlers
  const handleFilterChange = (key, value) => {
    setPage(0);
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setPage(0);
    setFilters({
      search: '',
      status: 'all',
      sortBy: 'name',
      sortOrder: 'asc',
    });
  };

  // Check if any filter is active
  const hasActiveFilters = filters.search || filters.status !== 'all';

  // Export handler
  const handleExport = async () => {
    setExporting(true);
    try {
      // Build export params from current filters
      const params = {
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
      };

      if (filters.search) params.search = filters.search;
      if (filters.status !== 'all') params.status = filters.status;

      const response = await scanCategoryAPI.export(params);

      // Create blob and trigger download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Generate filename with date
      const date = new Date().toISOString().split('T')[0];
      link.download = `scan_categories_export_${date}.xlsx`;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(t('scanCategories.exportSuccess', 'Scan categories exported successfully'));
    } catch (err) {
      console.error('Export error:', err);
      toast.error(
        err.response?.data?.message ||
        t('scanCategories.exportError', 'Failed to export scan categories')
      );
    } finally {
      setExporting(false);
    }
  };

  // Action handlers
  const handleOpenDialog = (category = null) => {
    setSelectedCategory(category);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedCategory(null);
  };

  const handleRestore = async (id) => {
    try {
      await scanCategoryAPI.restoreCategory(id);
      toast.success(t('scanCategories.restoreSuccess', 'Category restored successfully'));
      fetchCategories();
    } catch (err) {
      console.error('Restore error:', err);
      toast.error(err.response?.data?.message || t('scanCategories.restoreError', 'Failed to restore category'));
    }
  };

  const handleDelete = async () => {
    if (!selectedCategory) return;
    try {
      await scanCategoryAPI.delete(selectedCategory._id);
      toast.success(t('scanCategories.deleteSuccess', 'Category deleted successfully'));
      fetchCategories();
    } catch (err) {
      console.error('Delete error:', err);
      const backendMessage = err.response?.data?.message;

      if (backendMessage && backendMessage.toLowerCase().includes('has descendant scans')) {
        toast.error(t('scanCategories.hasScansError', 'Cannot delete category with associated scans'));
      } else {
        toast.error(backendMessage || t('scanCategories.deleteError', 'Failed to delete category'));
      }
    } finally {
      setOpenConfirm(false);
      setSelectedCategory(null);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Permission check
  if (!canView || privilegeError) {
    return <NoPrivilege message={t('scanCategories.noPrivilegeError', 'You do not have permission to view scan categories.')} />;
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h4" component="h1">
            {t('scanCategories.title', 'Scan Categories')}
          </Typography>
          {!loading && (
            <Chip
              label={`${total} ${t('common.total', 'total')}`}
              color="primary"
              variant="outlined"
              size="small"
            />
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          {/* Export Button */}
          {canExport && (
            <Button
              variant="outlined"
              startIcon={exporting ? <CircularProgress size={18} /> : <FileDownloadIcon />}
              onClick={handleExport}
              disabled={exporting || loading}
            >
              {exporting
                ? t('common.exporting', 'Exporting...')
                : t('common.export', 'Export')
              }
            </Button>
          )}
          {canAdd && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              {t('scanCategories.addCategoryButton', 'Add Category')}
            </Button>
          )}
        </Stack>
      </Box>

      {/* Filters Paper */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" alignItems="center" gap={2} mb={2}>
          <FilterList />
          <Typography variant="h6">{t('filters', 'Filters')}</Typography>
          {/* Filter indicator for export */}
          {hasActiveFilters && (
            <Chip
              label={t('common.filtersApplied', 'Filters will be applied to export')}
              size="small"
              color="info"
              variant="outlined"
            />
          )}
        </Stack>

        {/* Search and Sort Row */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <SearchBar
            onSearch={(query) => handleFilterChange('search', query)}
            initialValue={filters.search}
            placeholder={t('scanCategories.searchPlaceholder', 'Search categories...')}
            sx={{ flexGrow: 1, minWidth: '250px' }}
          />

          {/* Status Filter */}
          <Autocomplete
            options={STATUS_OPTIONS}
            getOptionLabel={(option) => option.label}
            value={STATUS_OPTIONS.find(opt => opt.value === filters.status)}
            onChange={(event, newValue) => handleFilterChange('status', newValue?.value || 'all')}
            renderInput={(params) => (
              <TextField {...params} label={t('common.status', 'Status')} size="small" />
            )}
            sx={{ minWidth: 150 }}
            disableClearable
          />

          <Autocomplete
            options={SORT_BY_OPTIONS}
            getOptionLabel={(option) => option.label}
            value={SORT_BY_OPTIONS.find(opt => opt.value === filters.sortBy)}
            onChange={(event, newValue) => handleFilterChange('sortBy', newValue?.value || 'name')}
            renderInput={(params) => (
              <TextField {...params} label={t('common.sortBy', 'Sort By')} size="small" />
            )}
            sx={{ minWidth: 180 }}
            disableClearable
          />

          <Autocomplete
            options={SORT_ORDER_OPTIONS}
            getOptionLabel={(option) => option.label}
            value={SORT_ORDER_OPTIONS.find(opt => opt.value === filters.sortOrder)}
            onChange={(event, newValue) => handleFilterChange('sortOrder', newValue?.value || 'asc')}
            renderInput={(params) => (
              <TextField {...params} label={t('common.order', 'Order')} size="small" />
            )}
            sx={{ minWidth: 150 }}
            disableClearable
          />

          {/* Clear Filters */}
          {hasActiveFilters && (
            <Button
              variant="text"
              color="inherit"
              onClick={handleClearFilters}
              startIcon={<ClearIcon />}
              size="small"
            >
              {t('common.clearFilters', 'Clear')}
            </Button>
          )}
        </Stack>
      </Paper>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
          {filters.search && (
            <Chip
              label={`${t('common.search', 'Search')}: ${filters.search}`}
              size="small"
              onDelete={() => handleFilterChange('search', '')}
            />
          )}
          {filters.status !== 'all' && (
            <Chip
              label={`${t('common.status', 'Status')}: ${STATUS_OPTIONS.find(o => o.value === filters.status)?.label}`}
              size="small"
              color={filters.status === 'active' ? 'success' : 'default'}
              onDelete={() => handleFilterChange('status', 'all')}
            />
          )}
        </Box>
      )}

      {/* Results Info */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('common.showing', 'Showing')} {total === 0 ? 0 : Math.min(page * rowsPerPage + 1, total)}-{Math.min((page + 1) * rowsPerPage, total)} {t('common.of', 'of')} {total} {t('common.items', 'items')}
      </Typography>

      {/* Table / No Content */}
      {categories.length === 0 && !loading ? (
        <Paper sx={{ p: 4 }}>
          <NoContent
            icon={<CategoryIcon sx={{ fontSize: 64 }} />}
            title={
              hasActiveFilters
                ? t('scanCategories.noFilterResults', 'No categories match your filters')
                : t('scanCategories.noCategoriesTitle', 'No Categories Found')
            }
            description={
              hasActiveFilters
                ? t('scanCategories.tryAdjustingFilters', 'Try adjusting your filter criteria')
                : t('scanCategories.noCategoriesDescription', 'Start by creating your first scan category.')
            }
            action={
              canAdd && !hasActiveFilters && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog()}
                >
                  {t('scanCategories.addCategoryButton', 'Add Category')}
                </Button>
              )
            }
          />
        </Paper>
      ) : (
        <ScanCategoriesTable
          categories={categories}
          loading={loading}
          canEdit={canEdit}
          canDelete={canDelete}
          onEdit={handleOpenDialog}
          onDelete={(category) => {
            setSelectedCategory(category);
            setOpenConfirm(true);
          }}
          onRestore={handleRestore}
          page={page}
          rowsPerPage={rowsPerPage}
          total={total}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          filters={filters}
        />
      )}

      {/* Dialogs */}
      <AddScanCategoryDialog
        open={openDialog}
        onClose={handleCloseDialog}
        selectedCategory={selectedCategory}
        onSuccess={fetchCategories}
      />

      <ConfirmDialog
        open={openConfirm}
        title={t('scanCategories.deleteCategoryTitle', 'Delete Category')}
        message={t('scanCategories.deleteCategoryContent', 'Are you sure you want to delete this category? This action cannot be undone.')}
        onConfirm={handleDelete}
        onClose={() => {
          setOpenConfirm(false);
          setSelectedCategory(null);
        }}
      />
    </Box>
  );
};

export default ScanCategories;